import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Bell, BellRing, BellDot, BellOff, HelpCircle, X, Video } from "lucide-react";
import { LogOut } from "lucide-react";

const helpContent = `

## How to use the dashboard

### Sign In
- Navigate to the sign-in page.
- Enter your credentials to log in.
- If you do not have an account, register for a new user.

---

## Overview

At the top of the interface, you will find the header with the following buttons:
- **Notifications** - View alerts and updates.
- **Add Drone** - Register a new drone.
- **Video Recordings** - View uploaded video highlights.
- **Help** - Access this guide.
- **Logout** - Sign out of your account.

To add a drone, click **Add Drone** and follow the prompts.

---

## Live Map (Powered by Leaflet)

### Navigation
- Use the **scroll wheel** to zoom in and out.
- Click and drag the map to move around.

### Setting Home Location
- Drag the **house icon** to your desired home location.
- Use the **"Go Home"** button in operator controls to return the drone to this location.

---

## Creating a Route

1. Click on the map to place waypoints.
2. If you make a mistake, click **Clear Waypoints**.
3. Once satisfied, click **Finalize Waypoints**.
4. Click **Save Route** to store the route.

---

## Loading a Route

- Select a route from the **Saved Routes** list.
- Click the **Load Route** button.

---

## Saving Default Map View

1. Enter the desired coordinates.
2. Adjust the map until the view matches your preference.
3. Click **Save Current Map as Default** (green button).

---

## Switching Between Camera and Map

- In the top-right corner, click either:
  - **Camera Preview**, or
  - **Live Map Preview**
- The view will switch seamlessly between the two.

---

## Operator Controls

### Control Modes
- **Joystick Mode** - Enables joystick-based control.
- **Standard Mode** - Enables regular/manual controls.

### Flight Modes
- **Manual Mode** - Direct user control of the drone.
- **Automatic Mode** - Drone follows predefined instructions or routes.

### Camera Controls
- Adjust the camera angle (up/down) using the camera control buttons.

---

## Command Log

- Displays a history of all commands issued.
- Useful for tracking actions and debugging.

---`;

export default function Header({
  title,
  onAddDrone,
  activeDroneName,
  activeDroneId,
  unreadCount = 0,
  onBellClick,
  onVideoRecordingsClick
}) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.reload();
  };

  return (
    <>
    <header className="App-header" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 20px'
    }}>
      
      {/* LEFT SIDE: Title + Drone Info */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <h1 style={{ margin: 0 }}>{title}</h1>
      </div>

{/* RIGHT SIDE: Buttons */}
<div style={{ display: 'flex', gap: '10px' }}>
<button
  onClick={onBellClick}
  className="header-button notification-btn"
  style={{ position: 'relative' }}
>
  <Bell size={18} color="#000000" strokeWidth={2} />

  {unreadCount > 0 && (
    <span className="notification-badge">
      {unreadCount}
    </span>
  )}
</button>

  <button onClick={onAddDrone} className="header-button">
    Add Drone
  </button>

  <button onClick={onVideoRecordingsClick} className="header-button"  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <Video size={18} />
  </button>

  <button onClick={() => setIsHelpOpen(true)} className="header-button" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <HelpCircle size={18} />
  </button>

  <button onClick={logout} className="header-button logout-btn">
    <LogOut size={18}/>
  </button>
</div>


    </header>

    {isHelpOpen && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '80%',
          maxHeight: '80%',
          overflow: 'auto',
          position: 'relative'
        }}>
          <button
            onClick={() => setIsHelpOpen(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer'
            }}
          >
          <X size={24} />
          </button>

          <h2>Help</h2>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <ReactMarkdown>{helpContent}</ReactMarkdown>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
